import { API_ENDPOINTS } from '@tsgecart/config';
import { adminApiFetch, adminApiUpload } from '../../api/admin-client.js';
import type {
  AdminMediaAsset,
  AdminMediaAssetPreview,
  AdminProductImage,
  MediaListParams,
} from '../types/media.js';

export interface FetchOptions {
  token: string;
  signal?: AbortSignal;
}

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export async function fetchAdminMedia(
  params: MediaListParams,
  options: FetchOptions,
): Promise<AdminMediaAsset[]> {
  const query = buildQuery({ ...params });
  return adminApiFetch<AdminMediaAsset[]>(`${API_ENDPOINTS.adminMedia}${query}`, options);
}

/** GET /admin/media/:id -- a single-asset lookup used for previews where only a media_asset_id is on hand (e.g. a product's OG image), not a product_images link row. */
export async function fetchAdminMediaAsset(
  id: string,
  options: FetchOptions,
): Promise<AdminMediaAssetPreview> {
  return adminApiFetch<AdminMediaAssetPreview>(`${API_ENDPOINTS.adminMedia}/${id}`, options);
}

/** Uploads a new image/video/PDF directly to R2 and returns its new media_assets row (see docs/media-r2-storage.md). */
export async function uploadAdminMedia(
  file: File,
  options: { token: string; signal?: AbortSignal },
): Promise<AdminMediaAsset> {
  return adminApiUpload<AdminMediaAsset>(`${API_ENDPOINTS.adminMedia}/upload`, { ...options, file });
}

export async function fetchProductImages(
  productId: string,
  options: FetchOptions,
): Promise<AdminProductImage[]> {
  return adminApiFetch<AdminProductImage[]>(
    `${API_ENDPOINTS.adminMedia}/products/${productId}/images`,
    options,
  );
}

export async function linkProductImage(
  productId: string,
  input: { mediaAssetId: string; altText?: string | null; asPrimary?: boolean },
  options: FetchOptions,
): Promise<AdminProductImage> {
  return adminApiFetch<AdminProductImage>(`${API_ENDPOINTS.adminMedia}/products/${productId}/images`, {
    ...options,
    method: 'POST',
    body: input,
  });
}

export async function reorderProductImages(
  productId: string,
  orderedImageIds: string[],
  options: FetchOptions,
): Promise<AdminProductImage[]> {
  return adminApiFetch<AdminProductImage[]>(
    `${API_ENDPOINTS.adminMedia}/products/${productId}/images/reorder`,
    { ...options, method: 'POST', body: { orderedImageIds } },
  );
}

export async function setPrimaryProductImage(
  productId: string,
  imageId: string,
  options: FetchOptions,
): Promise<AdminProductImage[]> {
  return adminApiFetch<AdminProductImage[]>(
    `${API_ENDPOINTS.adminMedia}/products/${productId}/images/${imageId}/primary`,
    { ...options, method: 'POST' },
  );
}

export async function updateProductImage(
  imageId: string,
  input: { altText: string | null },
  options: FetchOptions,
): Promise<AdminProductImage> {
  return adminApiFetch<AdminProductImage>(`${API_ENDPOINTS.adminMedia}/images/${imageId}`, {
    ...options,
    method: 'PATCH',
    body: input,
  });
}

export async function unlinkProductImage(
  imageId: string,
  options: FetchOptions,
): Promise<{ orphaned: boolean }> {
  return adminApiFetch<{ orphaned: boolean }>(`${API_ENDPOINTS.adminMedia}/images/${imageId}`, {
    ...options,
    method: 'DELETE',
  });
}
